/**
 * Diagnóstico de inscripciones que no se pudieron confirmar (Eduardo
 * 2026-10-23). Cada intento fallido recibe un FOLIO corto que ve la familia y
 * un aviso técnico al servidor (POST /public/registration-diagnostics).
 *
 * PRIVACIDAD: el aviso NUNCA lleva nada del formulario (nombres, correo,
 * celular, CURP, documentos). Solo etapa, tipo de falla, código HTTP, un
 * mensaje técnico corto, versión de la app, si había internet y la categoría.
 */
import { API_BASE_URL, ApiError, apiFetch } from "./api";

export type DiagnosticStage = "antes_de_enviar" | "durante_envio" | "al_procesar_respuesta";

export type DiagnosticKind =
  | "sin_conexion"
  | "tiempo_agotado"
  | "error_servidor"
  | "respuesta_invalida"
  | "error_pagina"
  | "conflicto"
  | "validacion";

export const DIAGNOSTIC_KIND_LABELS: Record<DiagnosticKind, string> = {
  sin_conexion: "Sin conexión",
  tiempo_agotado: "Tiempo agotado",
  error_servidor: "Error del servidor",
  respuesta_invalida: "Respuesta inválida",
  error_pagina: "Error de la página",
  conflicto: "Conflicto (ya existía)",
  validacion: "Datos rechazados",
};

export const DIAGNOSTIC_STAGE_LABELS: Record<DiagnosticStage, string> = {
  antes_de_enviar: "Antes de enviar",
  durante_envio: "Durante el envío",
  al_procesar_respuesta: "Al procesar la respuesta",
};

/** Tiempo máximo de espera del envío de la inscripción (con fotos o PDF). */
export const SUBMIT_TIMEOUT_MS = 60_000;

// Sin I, O, 0 ni 1: se dictan por teléfono sin confusión.
const FOLIO_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function generateFolio(random: () => number = Math.random): string {
  let folio = "";
  const cryptoObj = typeof globalThis.crypto !== "undefined" ? globalThis.crypto : undefined;
  if (random === Math.random && cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(6);
    cryptoObj.getRandomValues(bytes);
    for (const byte of bytes) folio += FOLIO_ALPHABET[byte % FOLIO_ALPHABET.length];
    return folio;
  }
  for (let index = 0; index < 6; index += 1) {
    folio += FOLIO_ALPHABET[Math.floor(random() * FOLIO_ALPHABET.length) % FOLIO_ALPHABET.length];
  }
  return folio;
}

/** La respuesta del servidor llegó pero no trae lo que esperábamos. */
export class InvalidResponseError extends Error {
  constructor(message = "Respuesta sin los datos esperados") {
    super(message);
    this.name = "InvalidResponseError";
  }
}

/** El envío tardó más de SUBMIT_TIMEOUT_MS y lo cortamos aquí. */
export class SubmitTimeoutError extends Error {
  constructor() {
    super("Tiempo de espera agotado");
    this.name = "SubmitTimeoutError";
  }
}

export interface ClassifiedFailure {
  kind: DiagnosticKind;
  stage: DiagnosticStage;
  httpStatus: number | null;
  /** Código de una lista cerrada (nunca el texto del error: podía traer
   * nombres o nombres de archivo — revisión #577). */
  errorCode: DiagnosticErrorCode;
}

export const DIAGNOSTIC_ERROR_CODES = [
  "network_error",
  "timeout",
  "aborted",
  "json_parse",
  "invalid_shape",
  "http_error",
  "js_typeerror",
  "js_referenceerror",
  "js_syntaxerror",
  "js_rangeerror",
  "js_error",
  "unknown",
] as const;
export type DiagnosticErrorCode = (typeof DIAGNOSTIC_ERROR_CODES)[number];

function jsErrorCode(err: unknown): DiagnosticErrorCode {
  if (err instanceof TypeError) return "js_typeerror";
  if (err instanceof ReferenceError) return "js_referenceerror";
  if (err instanceof SyntaxError) return "js_syntaxerror";
  if (err instanceof RangeError) return "js_rangeerror";
  if (err instanceof Error) return "js_error";
  return "unknown";
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function classifySubmitError(err: unknown, stage: DiagnosticStage): ClassifiedFailure {
  if (err instanceof ApiError) {
    const kind: DiagnosticKind =
      err.status === 409 ? "conflicto" : err.status >= 500 ? "error_servidor" : "validacion";
    return { kind, stage: "durante_envio", httpStatus: err.status, errorCode: "http_error" };
  }
  if (err instanceof SubmitTimeoutError) {
    return { kind: "tiempo_agotado", stage: "durante_envio", httpStatus: null, errorCode: "timeout" };
  }
  if (err instanceof DOMException && err.name === "AbortError") {
    return { kind: "tiempo_agotado", stage: "durante_envio", httpStatus: null, errorCode: "aborted" };
  }
  if (isOffline()) {
    return { kind: "sin_conexion", stage, httpStatus: null, errorCode: "network_error" };
  }
  if (err instanceof InvalidResponseError) {
    return { kind: "respuesta_invalida", stage: "al_procesar_respuesta", httpStatus: null, errorCode: "invalid_shape" };
  }
  if (err instanceof SyntaxError) {
    return { kind: "respuesta_invalida", stage: "al_procesar_respuesta", httpStatus: null, errorCode: "json_parse" };
  }
  // fetch() rechaza con TypeError cuando la red falla; solo cuenta así si
  // pasó mientras se enviaba (un TypeError en otra etapa es error nuestro).
  if (err instanceof TypeError && stage === "durante_envio") {
    return { kind: "sin_conexion", stage, httpStatus: null, errorCode: "network_error" };
  }
  return { kind: "error_pagina", stage, httpStatus: null, errorCode: jsErrorCode(err) };
}

const KIND_HINTS: Partial<Record<DiagnosticKind, string>> = {
  sin_conexion: "Parece que tu teléfono se quedó sin internet. Revisa tu conexión (mejor con Wi-Fi si subes foto o PDF).",
  tiempo_agotado: "El envío tardó demasiado. Puede ser la señal o el tamaño de los archivos.",
  error_servidor: "Nuestro servidor tuvo un problema al recibirla.",
  respuesta_invalida: "La respuesta del servidor llegó incompleta.",
  error_pagina: "La página tuvo un error inesperado.",
};

/** Texto para la familia. 409/422 conservan el mensaje específico del servidor. */
export function failureMessage(failure: ClassifiedFailure, folio: string, serverMessage?: string | null): string {
  if ((failure.kind === "conflicto" || failure.kind === "validacion") && serverMessage) {
    return serverMessage;
  }
  const hint = KIND_HINTS[failure.kind];
  return `No pudimos confirmar tu inscripción. Conserva esta referencia para que podamos revisar qué pasó: ${folio}.${hint ? ` ${hint}` : ""}`;
}

export interface DiagnosticPayload {
  folio: string;
  tournament_slug: string;
  division_id?: number | null;
  stage: DiagnosticStage;
  error_kind: DiagnosticKind;
  http_status?: number | null;
  error_code?: DiagnosticErrorCode | null;
  app_version?: string | null;
  online?: boolean | null;
  occurred_at?: string | null;
}

const FOLIO_RE = /^[A-HJ-NP-Z2-9]{6}$/;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,159}$/i;
const VERSION_RE = /^[A-Za-z0-9._-]{1,60}$/;
const STAGES: readonly DiagnosticStage[] = ["antes_de_enviar", "durante_envio", "al_procesar_respuesta"];

/**
 * Solo los campos permitidos, con valores de lista cerrada o formato fijo.
 * Lo que no cumpla se descarta (null): así nunca se guarda ni se manda
 * texto libre, tampoco desde una cola vieja del teléfono.
 */
export function controlledDiagnostic(raw: unknown): DiagnosticPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const folio = typeof item.folio === "string" && FOLIO_RE.test(item.folio) ? item.folio : null;
  const slug = typeof item.tournament_slug === "string" && SLUG_RE.test(item.tournament_slug) ? item.tournament_slug : null;
  const stage = STAGES.includes(item.stage as DiagnosticStage) ? (item.stage as DiagnosticStage) : null;
  const kind = item.error_kind && (item.error_kind as string) in DIAGNOSTIC_KIND_LABELS ? (item.error_kind as DiagnosticKind) : null;
  if (!folio || !slug || !stage || !kind) return null;
  const code = DIAGNOSTIC_ERROR_CODES.includes(item.error_code as DiagnosticErrorCode)
    ? (item.error_code as DiagnosticErrorCode)
    : null;
  const status = typeof item.http_status === "number" && Number.isInteger(item.http_status) && item.http_status >= 100 && item.http_status <= 599 ? item.http_status : null;
  const division = typeof item.division_id === "number" && Number.isInteger(item.division_id) && item.division_id > 0 ? item.division_id : null;
  const version = typeof item.app_version === "string" && VERSION_RE.test(item.app_version) ? item.app_version : null;
  const occurred = typeof item.occurred_at === "string" && !Number.isNaN(Date.parse(item.occurred_at)) ? new Date(item.occurred_at).toISOString() : null;
  return {
    folio,
    tournament_slug: slug,
    division_id: division,
    stage,
    error_kind: kind,
    http_status: status,
    error_code: code,
    app_version: version,
    online: typeof item.online === "boolean" ? item.online : null,
    occurred_at: occurred,
  };
}

export function appVersion(): string {
  const fromEnv = import.meta.env.VITE_APP_VERSION as string | undefined;
  if (fromEnv && VERSION_RE.test(fromEnv)) return fromEnv;
  if (typeof document !== "undefined") {
    const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
    const name = script?.src.split("/").pop()?.split("?")[0];
    if (name && VERSION_RE.test(name)) return name;
  }
  return "dev";
}

export function buildDiagnostic(params: {
  folio: string;
  slug: string;
  divisionId?: number | null;
  failure: ClassifiedFailure;
}): DiagnosticPayload | null {
  return controlledDiagnostic({
    folio: params.folio,
    tournament_slug: params.slug,
    division_id: params.divisionId && params.divisionId > 0 ? params.divisionId : null,
    stage: params.failure.stage,
    error_kind: params.failure.kind,
    http_status: params.failure.httpStatus,
    error_code: params.failure.errorCode,
    app_version: appVersion(),
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    occurred_at: new Date().toISOString(),
  });
}

export const DIAGNOSTIC_QUEUE_KEY = "t123:diagnosticos-inscripcion";
const QUEUE_MAX = 10;

function readQueue(): DiagnosticPayload[] {
  try {
    const raw = window.localStorage.getItem(DIAGNOSTIC_QUEUE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(controlledDiagnostic).filter((item): item is DiagnosticPayload => item !== null);
  } catch {
    return [];
  }
}

function writeQueue(items: DiagnosticPayload[]): void {
  try {
    if (items.length === 0) window.localStorage.removeItem(DIAGNOSTIC_QUEUE_KEY);
    else {
      const safe = items.map(controlledDiagnostic).filter((item): item is DiagnosticPayload => item !== null);
      window.localStorage.setItem(DIAGNOSTIC_QUEUE_KEY, JSON.stringify(safe.slice(-QUEUE_MAX)));
    }
  } catch {
    // Navegador privado o almacenamiento lleno: el aviso se pierde, la
    // familia conserva su folio.
  }
}

export function queuedDiagnostics(): DiagnosticPayload[] {
  return readQueue();
}

async function postDiagnostic(payload: DiagnosticPayload, token?: string | null): Promise<boolean> {
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE_URL}/public/registration-diagnostics`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
    // 4xx (inválido, límite) no se reintenta: no se arreglaría solo.
    return response.ok || (response.status >= 400 && response.status < 500);
  } catch {
    return false;
  }
}

/** Manda el aviso; si no hay internet (o falla la red) lo deja en cola. */
export async function reportDiagnostic(raw: DiagnosticPayload | null, token?: string | null): Promise<void> {
  const payload = controlledDiagnostic(raw);
  if (!payload) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    writeQueue([...readQueue(), payload]);
    return;
  }
  const sent = await postDiagnostic(payload, token);
  if (!sent) writeQueue([...readQueue(), payload]);
}

/** Envía lo que quedó en cola (al volver el internet o al abrir la página). */
export async function flushDiagnosticQueue(token?: string | null): Promise<void> {
  const pending = readQueue();
  if (pending.length === 0) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const remaining: DiagnosticPayload[] = [];
  for (const item of pending) {
    if (!(await postDiagnostic(item, token))) remaining.push(item);
  }
  writeQueue(remaining);
}

/**
 * Corre `task` con un AbortSignal que se dispara a los `ms`. Si se agota,
 * rechaza con SubmitTimeoutError (aunque la petición no respete la señal).
 */
export function withTimeout<T>(task: (signal: AbortSignal) => Promise<T>, ms = SUBMIT_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new SubmitTimeoutError());
    }, ms);
  });
  return Promise.race([task(controller.signal), timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

// ── Consulta (superadmin y organizador) ────────────────────────────────

export interface DiagnosticItem {
  id: number;
  folio: string;
  created_at: string | null;
  occurred_at: string | null;
  tournament_id: number | null;
  tournament_slug: string;
  tournament_name: string | null;
  division_id: number | null;
  stage: DiagnosticStage;
  error_kind: DiagnosticKind;
  http_status: number | null;
  error_code: DiagnosticErrorCode | null;
  app_version: string | null;
  browser?: string | null;
  os?: string | null;
  online: boolean | null;
  user_id?: number | null;
}

export interface DiagnosticListData {
  items: DiagnosticItem[];
  counts: Record<DiagnosticKind, number>;
  total: number;
  tournaments?: { id: number; name: string }[];
  days?: number;
}

export interface DiagnosticFilters {
  tournamentId?: number | null;
  kind?: DiagnosticKind | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  folio?: string | null;
  limit?: number;
  offset?: number;
}

export function listSuperadminDiagnostics(token: string, filters: DiagnosticFilters = {}) {
  const params = new URLSearchParams();
  if (filters.tournamentId) params.set("tournament_id", String(filters.tournamentId));
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.folio?.trim()) params.set("folio", filters.folio.trim().toUpperCase());
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.offset) params.set("offset", String(filters.offset));
  const query = params.toString();
  return apiFetch<{ ok: boolean; data: DiagnosticListData }>(
    `/superadmin/registration-diagnostics${query ? `?${query}` : ""}`,
    { token },
  );
}

export function listTournamentDiagnostics(token: string, tournamentId: number, days = 7) {
  return apiFetch<{ ok: boolean; data: DiagnosticListData }>(
    `/admin/tournaments/${tournamentId}/registration-diagnostics?days=${days}`,
    { token },
  );
}
